import React from 'react';
import { Dialog } from '@headlessui/react';

import { BackIcon, useIsEmployerApp, cn } from '@restworld/utils-common';
import { Button } from '../../button';
import { XMarkIcon } from '@heroicons/react/24/outline';
import IconButton, { IconComponentType } from '../../icon-button';

export type DesktopModalVariant = 'center' | 'right';

export interface DesktopModalProps {
  open: boolean;
  title?: string | React.ReactNode;
  onClose: () => void;
  variant?: DesktopModalVariant;
  CloseIcon?: IconComponentType;
  children?: React.ReactNode;
  modalStyles?: string[];
  onBack?: () => void;
  showBackButton?: boolean;
  backIcon?: React.ReactNode;
  noPadding?: boolean;
  withTransition?: boolean;
  hideHeader?: boolean;
  showCloseIcon?: boolean;
  footer?: React.ReactNode;
  footerStyles?: string[];
  primaryActionLabel?: string;
  onClickPrimaryAction?: () => void;
  secondaryActionLabel?: string;
  onClickSecondaryAction?: () => void;
  headerStyles?: string[];
  modalBodyStyles?: string[];
  hasOffWhiteBackground?: boolean;
  // Headless UI v2 applies the enter/leave transition classes (e.g. the right-drawer
  // translate-x-full -> translate-x-0 slide) via the child's className when the Transition uses
  // `as={Fragment}`. This component must forward that className to the panel or the slide is lost.
  className?: string;
}

const DesktopModal = React.forwardRef(
  (
    {
      onClose,
      variant = 'center',
      title,
      CloseIcon,
      children,
      modalStyles,
      backIcon,
      showBackButton,
      onBack,
      noPadding,
      hideHeader = false,
      showCloseIcon,
      footer,
      footerStyles,
      primaryActionLabel,
      onClickPrimaryAction,
      secondaryActionLabel,
      onClickSecondaryAction,
      headerStyles,
      modalBodyStyles,
      hasOffWhiteBackground,
      className,
    }: DesktopModalProps,
    ref
  ) => {
    const { isEmployerApp } = useIsEmployerApp();

    return (
      <StyledDesktopDialogPanel
        variant={variant}
        ref={ref as React.RefObject<HTMLDivElement | null>}
        modalstyles={modalStyles}
        hasOffWhiteBackground={hasOffWhiteBackground || isEmployerApp}
        className={className}
      >
        {variant === 'center' && !hideHeader && (
          <CenteredModalHeader
            {...{
              title,
              onClose,
              CloseIcon,
              backIcon,
              showBackButton,
              onBack,
              noPadding,
              headerStyles,
              showCloseIcon,
              hasOffWhiteBackground,
            }}
          />
        )}
        {variant === 'right' && !hideHeader && (
          <RightModalHeader
            title={title}
            onClose={onClose}
            CloseIcon={CloseIcon}
            showCloseIcon={showCloseIcon}
            headerStyles={headerStyles}
            hasOffWhiteBackground={hasOffWhiteBackground}
          />
        )}
        <DesktopModalBody variant={variant} noPadding={noPadding} modalBodyStyles={modalBodyStyles}>
          {children}
        </DesktopModalBody>
        {(footer || primaryActionLabel || secondaryActionLabel) && (
          <DesktopModalFooter variant={variant} noPadding={noPadding} footerStyles={footerStyles}>
            {footer}
            {(primaryActionLabel || secondaryActionLabel) && (
              <div className="flex flex-col items-start w-full gap-4">
                {primaryActionLabel && (
                  <Button
                    fullwidth
                    title={primaryActionLabel}
                    variant="normal"
                    onClick={() => {
                      if (onClickPrimaryAction) {
                        onClickPrimaryAction();
                      }
                    }}
                  />
                )}
                {secondaryActionLabel && (
                  <Button
                    fullwidth
                    title={secondaryActionLabel}
                    variant="secondary-outline"
                    onClick={() => {
                      if (onClickSecondaryAction) {
                        onClickSecondaryAction();
                      }
                    }}
                  />
                )}
              </div>
            )}
          </DesktopModalFooter>
        )}
      </StyledDesktopDialogPanel>
    );
  }
);

export default DesktopModal;

interface CenteredModalHeaderProps {
  CloseIcon?: IconComponentType;
  onClose: () => void;
  title?: string | React.ReactNode;
  onBack?: () => void;
  showBackButton?: boolean;
  backIcon?: React.ReactNode;
  noPadding?: boolean;
  headerStyles?: string[];
  showCloseIcon?: boolean;
  hasOffWhiteBackground?: boolean;
}

const CenteredModalHeader = ({
  CloseIcon,
  onClose,
  title,
  onBack,
  showBackButton,
  backIcon,
  noPadding,
  headerStyles,
  showCloseIcon = true,
  hasOffWhiteBackground,
}: CenteredModalHeaderProps) => {
  const { isEmployerApp } = useIsEmployerApp();

  return (
    <CenteredHeaderStyled headerStyles={headerStyles} $hasOffWhiteBackground={hasOffWhiteBackground || isEmployerApp}>
      <StyledButtonContainer noPadding={noPadding} showBackButton={showBackButton}>
        {showBackButton && (
          <div className="border-none outline-none cursor-pointer" onClick={() => !!onBack && onBack()}>
            {backIcon || <BackIcon />}
          </div>
        )}
        {showCloseIcon && <IconButton ariaLabel="" Icon={CloseIcon || XMarkIcon} onClick={() => onClose()} />}
      </StyledButtonContainer>
      {!!title && (
        <div className="flex flex-row w-full justify-center">
          {typeof title === 'string' ? (
            <h3 className="text-gray-900 font-semibold text-xl leading-6 text-center">{title}</h3>
          ) : (
            title
          )}
        </div>
      )}
    </CenteredHeaderStyled>
  );
};

const CenteredHeaderStyled = ({ headerStyles, $hasOffWhiteBackground, className, ...props }: any) => (
  <div
    className={cn(
      'sticky top-0 z-10 bg-white flex w-full flex-col items-start justify-start gap-2 pb-2',
      $hasOffWhiteBackground ? 'bg-neutral-10' : '',
      className
    )}
    {...props}
  />
);

interface RightModalHeaderProps {
  CloseIcon?: IconComponentType;
  onClose: () => void;
  title?: string | React.ReactNode;
  showCloseIcon?: boolean;
  headerStyles?: string[];
  hasOffWhiteBackground?: boolean;
}

const RightModalHeader = ({
  showCloseIcon = true,
  CloseIcon,
  onClose,
  title,
  headerStyles,
  hasOffWhiteBackground,
}: RightModalHeaderProps) => {
  const { isEmployerApp } = useIsEmployerApp();

  return (
    <RightHeaderStyled headerStyles={headerStyles} $hasOffWhiteBackground={hasOffWhiteBackground || isEmployerApp}>
      {showCloseIcon ? <IconButton ariaLabel="" Icon={CloseIcon || XMarkIcon} onClick={() => onClose()} /> : null}
      {typeof title === 'string' ? <h3 className="text-gray-900 font-semibold text-xl leading-6">{title}</h3> : title}
    </RightHeaderStyled>
  );
};
const RightHeaderStyled = ({ headerStyles, $hasOffWhiteBackground, className, ...props }: any) => (
  <div
    className={cn(
      'sticky top-0 pt-12 z-10 bg-white  w-full flex flex-row items-center justify-start gap-12 pb-2 px-8',
      $hasOffWhiteBackground ? 'bg-neutral-10' : '',
      className
    )}
    {...props}
  />
);
const StyledDesktopDialogPanel = React.forwardRef<HTMLDivElement, any>(
  ({ variant, modalstyles, hideHeader, hasOffWhiteBackground, className, ...props }, ref) => (
    <Dialog.Panel
      ref={ref}
      className={cn(
        'flex flex-col items-start justify-start box-border bg-white min-w-104 overflow-hidden',
        variant === 'center' && 'min-h-fit rounded-3.75 lg:max-h-[calc(100vw-200px)]',
        variant === 'right' && 'min-h-full h-full fixed rounded-tl-6.25 pb-4 pt-0 w-[391px]',
        variant === 'right' && hideHeader && 'pt-12',
        hasOffWhiteBackground && 'bg-neutral-10',
        ...(modalstyles || []),
        className
      )}
      {...props}
    />
  )
);

const DesktopModalBody = ({ variant, noPadding, modalBodyStyles, className, ...props }: any) => (
  <div
    className={cn(
      'flex flex-col flex-1 max-w-full grow overflow-y-auto items-start box-border w-full pb-2 overflow-x-hidden',
      variant === 'center' ? 'px-8' : '',
      variant === 'right' ? 'px-8' : '',
      noPadding ? '!px-0 !py-0' : '',
      // Caller-provided body cap (e.g. md:max-h-[calc(90vh-380px)]) — dropped in the twin→className
      // migration; without it the panel's min-h-fit grows past max-h and the body never scrolls.
      ...(modalBodyStyles || []),
      className
    )}
    {...props}
  />
);

export const StyledButtonContainer = ({ showBackButton, noPadding, className, ...props }: any) => (
  <div
    className={cn(
      'flex flex-row w-full items-start justify-end box-border flex-1 pt-4 px-4',
      showBackButton ? 'justify-between' : '',
      noPadding ? 'pt-0 px-0' : '',
      className
    )}
    {...props}
  />
);

const DesktopModalFooter = ({ variant, noPadding, footerStyles, className, ...props }: any) => (
  <div
    className={cn(
      //
      'flex flex-col items-start box-border w-full pt-4 pb-4 border-t border-[#DEDEE1]',
      variant === 'center' ? 'px-8' : '',
      variant === 'right' ? 'px-8' : '',
      noPadding ? '!px-0 !py-0' : '',
      className
    )}
    {...props}
  />
);
